import { useEffect, useRef, useState } from 'react';
import styles from './GSTR3BTable.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { infoIconGreen, infoIconYellow, infoToolTip } from '../../../../../assets/hostedassets';
// import { headers } from './data';
import clsx from 'clsx';
import { fetchGstr3bData, getGSTReports } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import { buildGstRangeParams } from '../../../../../utils/dateUtils';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { useParams } from 'react-router-dom';
import { GSTR3B_MONTHS_PER_PAGE, infoTooltipGStR3b } from './gstr3Constants';
import dayjs from 'dayjs';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { formatINR } from '../RevenueVendorExpenseTable/components/overlap.constants';
import { GST_RANGE_KEYS, GstRangeKey } from '../../GstOverview/useGstDateRange';

type tableHeaders = { value: string; type: string }[];
type MetricsData = Record<string, MetricData>;
type MetricData = {
    key: string;
    value: string;
    amounts: Record<string, string>;
};

interface Gstr3bTableData {
    headers: tableHeaders;
    metricsData: MetricsData;
    transformedGstData: Record<string, { gsts: { gst: string; state: string | null }[] }>;
}

interface GstConnectionData {
    totalCount: number;
    totalGstList: { gstNumber: string; gstStatus: string; connected: boolean }[];
    gstMonthErrors?: Record<string, Record<string, { errorCd?: string; message?: string }>>;
}

const GST_DATA = 'GST_DATA';
const GST_DATA_MODULE = 'GST_DATA_MODULE';
const UW_GSTR3B_DATA = 'UW_GSTR3B_DATA';
const AION_GET_GST_LIST = 'AION_GET_GST_LIST';

interface Gstr3BTableProps {
    dataSource: any;
    fetchHeadlinePanelData?: (isPolling?: boolean) => void;
    rangeKey?: GstRangeKey;
    fetchStartDate?: string;
    fetchEndDate?: string;
}

// rangeKey/fetchStartDate/fetchEndDate: applied GST "Time Period" range (RECUR-27384).
// ALL now sends `rangeKey` too (product decision) so it hits the server-side ALL cache —
// this is a no-op through the polus unifier on the CURRENT live template (unknown payload
// keys are dropped) and becomes meaningful post-cutover, when the new UW treats ALL as
// no-window-but-cached. The rangeKey → params mapping itself lives in buildGstRangeParams,
// shared with the "Excel Analysis" report call so the two can never diverge.
function Gstr3BTable({
    dataSource,
    fetchHeadlinePanelData,
    rangeKey = GST_RANGE_KEYS.ALL,
    fetchStartDate = '',
    fetchEndDate = '',
}: Gstr3BTableProps) {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [gstr3bTableData, setGstr3bTableData] = useState<Gstr3bTableData | null>(null);
    const [gstConnectionData, setGstConnectionData] = useState<GstConnectionData | null>(null);
    const { headers, metricsData, transformedGstData } = gstr3bTableData || {};
    const noDataAvailable = !loading && !headers?.length;
    const dataSourceList = gstConnectionData?.totalGstList || [];

    const assesseeOrgId = params.id;
    let pollingIntervalRef: any = useRef(null);
    let pollingCount = useRef(0);
    // RECUR-27384 review fix: bumped on every fetch trigger (mount, range change) so an
    // out-of-order response from a superseded range/generation is dropped instead of
    // overwriting newer data. Polling refreshes belong to whichever generation is current
    // at the moment they resolve (captured via the default param below).
    const fetchGenRef = useRef(0);

    // Report-generation status (drives the processing/polling spinner) is range-independent
    // — it only needs to react to mount/org change, not to the applied Time Period range
    // (RECUR-27384 review fix).
    useEffect(() => {
        if (assesseeOrgId) {
            fetchGSTReports();
        }

        return () => {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            pollingCount.current = 0;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assesseeOrgId]);

    // The gstr3b data fetch is keyed on the applied Time Period range.
    useEffect(() => {
        if (!assesseeOrgId) return;
        // A CUSTOM range must only be applied once BOTH month chips are set. Picking one
        // side flips rangeKey to CUSTOM before the second date lands, and this effect (which
        // reacts to rangeKey) would otherwise fire a CUSTOM fetch carrying an empty/one-sided
        // window — which the server returns no data for. Hold the previously-applied range
        // until both dates are present (RECUR-27384).
        if (rangeKey === GST_RANGE_KEYS.CUSTOM && (!fetchStartDate || !fetchEndDate)) return;
        fetchGenRef.current += 1;
        fetchData(assesseeOrgId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assesseeOrgId, rangeKey, fetchStartDate, fetchEndDate]);

    const fetchData = (assesseeOrgId, isPollingCall = false, gen = fetchGenRef.current) => {
        if (!isPollingCall) setLoading(true);
        else pollingCount.current += 1;
        fetchGstr3bData({ orgId: assesseeOrgId, ...buildGstRangeParams(rangeKey, fetchStartDate, fetchEndDate) }, {})
            .then((data) => {
                if (gen !== fetchGenRef.current) return; // stale response from a superseded range — drop it
                let gstr3bData = data?.[UW_GSTR3B_DATA]?.data || {};
                let gstConnectionData = data?.[AION_GET_GST_LIST] || {};

                gstr3bData.headers = gstr3bData?.headers || [];
                setGstr3bTableData(gstr3bData);
                setGstConnectionData(gstConnectionData);
            })
            .catch((err) => console.error('Error in fetching gst3b data', err))
            .finally(() => {
                if (gen === fetchGenRef.current) setLoading(false);
            });
    };

    // RECUR-27384 review fix: the reports/polling effect only depends on [assesseeOrgId]
    // (finding 4), so its setInterval closure is created once per org and never recreated on
    // a range change — meaning a plain `fetchData` reference captured at that time would stay
    // frozen on the range that was applied at mount/org-change, even though buildRangeParams()
    // (and thus its payload) reads rangeKey/fetchStartDate/fetchEndDate by closure. The
    // generation guard alone doesn't catch this: a poll tick after a range change still reads
    // the CURRENT (already-bumped) generation, since it fires after the bump — it validates
    // sequencing, not which params were used. Keep this ref reassigned every render so the
    // interval always calls the LATEST fetchData closure (current rangeKey/dates), not a stale one.
    // Assigned in an effect — not during render — so under React 18 concurrent rendering an
    // abandoned render can never leave the interval pointing at an uncommitted closure.
    const fetchDataRef = useRef(fetchData);
    useEffect(() => {
        fetchDataRef.current = fetchData;
    });

    const fetchGSTReports = () => {
        getGSTReports({ assesseeOrgId: assesseeOrgId }, {})
            .then((data) => {
                if (data?.some((item) => item.status === 'PROCESSING')) {
                    if (!pollingIntervalRef.current)
                        pollingIntervalRef.current = setInterval(() => {
                            fetchGSTReports();
                            fetchDataRef.current(assesseeOrgId, true);
                            fetchHeadlinePanelData?.(true);
                        }, 10000);
                } else {
                    if (pollingIntervalRef.current || pollingCount.current >= 32) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                        pollingCount.current = 0;
                    }
                }
            })
            .catch((err) => {});
    };

    const renderTooltip = (gsts = [], month?: string) => {
        return (
            <div className={styles.Tooltip}>
                <div className={styles.TopText}>Charts/table are generated only for GST's with Data Available:</div>
                <div className={styles.Gsts}>
                    {dataSourceList?.map((gst: any, i) => {
                        const monthlyGst: any = gsts?.find((monthlyGst: any) => monthlyGst.gst === gst.gstNumber);
                        const monthError = month ? gstConnectionData?.gstMonthErrors?.[gst.gstNumber]?.[month] : undefined;
                        return (
                            <div className={styles.GstRow} key={i}>
                                <div className={styles.Left}>
                                    <span className={styles.State}>{gst?.state} </span>
                                    <span>{gst?.gstNumber}</span>
                                </div>
                                <div className={styles.Right}>
                                    <div
                                        className={clsx(styles.Badge, {
                                            [styles.NotConnected]: !monthlyGst && !gst?.connected,
                                            [styles.NoData]: !monthlyGst && gst?.connected,
                                            [styles.Connected]: monthlyGst?.dataSource === 'CONNECTED',
                                            [styles.Uploaded]: monthlyGst?.dataSource === 'UPLOADED',
                                        })}
                                    >
                                        {monthlyGst?.dataSource === 'CONNECTED' ? (
                                            'Data Available'
                                        ) : monthlyGst?.dataSource === 'UPLOADED' ? (
                                            'Data Uploaded'
                                        ) : gst?.connected ? (
                                            <>
                                                No Data
                                                {monthError?.message && (
                                                    <Tooltip content={monthError.message} direction={'top'} fitContent small usePortal>
                                                        <button type="button" className={styles.ErrorIconButton}>
                                                            <img
                                                                src={infoToolTip}
                                                                alt="info"
                                                                height={'13px'}
                                                                className={styles.ErrorIcon}
                                                            />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                            </>
                                        ) : (
                                            'Not Connected'
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const getCountValue = (header, headersLength, index) => {
        const monthlyGstCount = transformedGstData?.[header]?.gsts?.length ?? '0';
        const totalGstCount = dataSourceList?.length ?? '-';
        return (
            <div
                className={clsx(styles.DataCountBadge, {
                    [styles.Green]: totalGstCount === monthlyGstCount,
                    [styles.Yellow]: totalGstCount !== monthlyGstCount,
                })}
            >
                {monthlyGstCount}/{totalGstCount}
                <Tooltip
                    content={renderTooltip(transformedGstData?.[header]?.gsts as [], header)}
                    direction={'top'}
                    style={{ top: `20px`, left: headersLength - index < 5 ? '-100px' : '' }}
                >
                    <img
                        className={styles.InfoIcon}
                        src={totalGstCount === monthlyGstCount ? infoIconGreen : infoIconYellow}
                        alt={''}
                        height={'14px'}
                    />
                </Tooltip>
            </div>
        );
    };

    const headerLeft = (
        <div className={styles.Left}>
            <span className={styles.Title}>GSTR-3B Summary</span>
            <Tooltip content={infoTooltipGStR3b} direction={'right'}>
                <img src={infoToolTip} alt="info" height={'18px'} />
            </Tooltip>
        </div>
    );

    const headerRight = (
        <div className={styles.Right}>
            {loading || pollingIntervalRef.current ? (
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                    <LoadingSpinner color={'#6021b3'} height={'20px'} />
                    {pollingIntervalRef.current && <span className={styles.Nodata}>Processing (may take ~2 mins)</span>}
                </div>
            ) : headers?.length ? (
                ''
            ) : (
                <span className={styles.Nodata}>No data available</span>
            )}
        </div>
    );

    const formatAmount = (amount?: string) => {
        if (amount === undefined || amount === null || amount === '' || amount === '-') return '-';
        const num = Number(amount);
        return Number.isNaN(num) ? amount : formatINR(num);
    };

    // Fewer month columns than a full page (RECUR-27384): stretch them to fill the table width
    // instead of leaving fixed-width gaps. A full/paginated response (>= GSTR3B_MONTHS_PER_PAGE
    // month columns) keeps the existing fixed-width, horizontally-scrollable layout as-is.
    const monthColumnCount = headers?.filter((header) => header.type !== 'classification' && header.type !== 'total').length ?? 0;
    const isCompact = monthColumnCount > 0 && monthColumnCount < GSTR3B_MONTHS_PER_PAGE;

    const tableContent = !loading && headers?.length && (
        <div className={clsx(styles.TableContainer, { [styles.Compact]: isCompact })}>
            <div className={styles.Headers}>
                {headers?.map((header, i) => (
                    <div
                        key={i}
                        className={clsx(styles.Header, {
                            [styles.Classification]: header.type === 'classification',
                            [styles.Total]: header.type === 'total',
                        })}
                    >
                        {header.type === 'period' ? dayjs(header.value, 'MM-YYYY').format("MMM'YY") : header.value}
                    </div>
                ))}
            </div>

            <div className={styles.Rows}>
                <div className={styles.Row}>
                    {headers?.map((header, i) => (
                        <div
                            key={`row-item-${i}`}
                            className={clsx(styles.RowItem, {
                                [styles.Classification]: header.type === 'classification',
                                [styles.Total]: header.type === 'total',
                            })}
                        >
                            {header.type === 'classification'
                                ? 'Data Available'
                                : header.type === 'period'
                                ? getCountValue(header.value, headers?.length, i)
                                : '-'}
                        </div>
                    ))}
                </div>
                {Object.entries(metricsData || {}).map(([key, metricData], i) => (
                    <div className={styles.Row} key={`metric-row-${key}`}>
                        {headers?.map((header, j) => (
                            <div
                                key={`metric-item-${key}-${j}`}
                                className={clsx(styles.RowItem, {
                                    [styles.Classification]: header.type === 'classification',
                                    [styles.Total]: header.type === 'total',
                                })}
                            >
                                {header.type === 'classification'
                                    ? metricData.value
                                    : formatAmount(metricData?.amounts?.[header.value] ?? metricData?.amounts?.[header.type])}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className={styles.Gstr3b}>
            <HeaderBodyBox
                title={headerLeft}
                headerRight={headerRight}
                className={clsx({
                    [styles.TopNoData]: loading || noDataAvailable,
                })}
            >
                {!!(!loading && headers?.length) && tableContent}
            </HeaderBodyBox>
        </div>
    );
}

export default Gstr3BTable;
